import { useContext, useMemo } from 'react';

import { ELayoutManagerMode } from '#shared';

import {
  LayoutEditingContext,
  type LayoutEditingContextProps,
} from '../../Settings';

/**
 * Hook to access layout mode and editing context.
 *
 * @description
 * This hook provides access to the layout editing context when available (Settings page)
 * or returns a safe fallback with no-op implementations for RENDER mode.
 *
 * **Usage Patterns:**
 * - In Settings page: Returns real editing context with functional handlers
 * - In other pages (Map, etc.): Returns stub context with safe no-op handlers
 *
 * **Important:**
 * Components should always check `mode === ELayoutManagerMode.EDIT` before calling
 * methods like `handleTapPlace`, `setSelectedPaletteWidget`, etc., as they will be
 * no-ops outside of the Settings editing context.
 *
 * @param props - Optional props to override mode (defaults to RENDER when no context exists)
 * @returns Layout editing context (real or stub implementation)
 *
 * @example
 * ```tsx
 * const { mode, handleTapPlace } = useLayoutMode();
 *
 * const handleClick = () => {
 *   if (mode === ELayoutManagerMode.EDIT) {
 *     handleTapPlace(sectionId, position); // Only call in EDIT mode
 *   }
 * };
 * ```
 */
export const useLayoutMode = (props?: {
  mode?: ELayoutManagerMode;
}): LayoutEditingContextProps => {
  // Try to get editing context (only exists in Settings)
  const editingContext = useContext(LayoutEditingContext);

  // Fallback mode for when no context exists
  const fallbackMode = props?.mode ?? ELayoutManagerMode.RENDER;

  // Memoize stub context to prevent unnecessary re-renders
  // Must be called unconditionally (Rules of Hooks)
  const stubContext = useMemo<LayoutEditingContextProps>(
    () => ({
      mode: fallbackMode,
      selectedWidgetId: null,
      setSelectedWidgetId: () => {
        // No-op: editing not available outside Settings context
      },
      selectedPaletteWidget: null,
      setSelectedPaletteWidget: () => {
        // No-op: editing not available outside Settings context
      },
      handleTapPlace: () => {
        // No-op: editing not available outside Settings context
      },
      handleTapReplace: () => {
        // No-op: editing not available outside Settings context
      },
      handleTapSwap: () => {
        // No-op: editing not available outside Settings context
      },
      pendingPlacement: null,
      setPendingPlacement: () => {
        // No-op: editing not available outside Settings context
      },
      handleDeleteWidget: () => {
        // No-op: editing not available outside Settings context
      },
      showToast: () => {
        // No-op: editing not available outside Settings context
      },
    }),
    [fallbackMode],
  );

  // If we have editing context, use it (Settings page with real implementations)
  // Otherwise return stub context for RENDER mode
  return editingContext ?? stubContext;
};
